import React, { useState } from 'react';
import { getIconForType } from './icons';
import { objectProperties } from '../../mock/mockData';
import { detailedProperties } from '../../mock/detailedProperties';

const TABS = ['General', 'Address', 'Account', 'Profile', 'Telephones', 'Organization', 'Member Of', 'Dial-in', 'Environment', 'Sessions'];

const PropertiesDialog = ({ open, item, onClose, typeLabels }) => {
  const [active, setActive] = useState('General');
  if (!open || !item) return null;

  const props = objectProperties[item.id] || null;
  const detail = detailedProperties[item.id] || null;
  const isUser = item.type === 'USER';
  const isGroup = item.type === 'GROUP';
  const isComputer = item.type === 'COMPUTER';
  const isOU = item.type === 'OU';

  // Tabs depend on object type (ADUC does this)
  let tabs = TABS;
  if (isGroup) tabs = ['General', 'Members', 'Member Of', 'Managed By'];
  else if (isComputer) tabs = ['General', 'Operating System', 'Member Of', 'Delegation', 'Location', 'Managed By'];
  else if (isOU) tabs = ['General', 'Managed By', 'COM+', 'Group Policy'];

  // Specialized tabs for cert/edu/contact (kind=cert|edu|contact) still use USER type
  if (detail && detail.kind === 'role') tabs = ['General', 'Responsibilities', 'Tools', 'Frameworks', 'Metrics', 'Member Of'];
  else if (detail && detail.kind === 'cert') tabs = ['General', 'Verification', 'Member Of'];
  else if (detail && detail.kind === 'edu') tabs = ['General', 'Coursework', 'Member Of'];
  else if (detail && detail.kind === 'contact') tabs = ['General'];
  else if (detail && detail.kind === 'project') tabs = ['General', 'Operating System', 'Technologies', 'Repository'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div
        className="bg-[#F0F0F0] border border-[#808080] shadow-2xl"
        style={{ width: 460, fontFamily: 'Tahoma, Segoe UI, sans-serif' }}
      >
        {/* Title bar */}
        <div className="flex items-center justify-between h-[26px] bg-[#2B579A] text-white px-[8px]">
          <div className="flex items-center gap-[6px]">
            <span className="scale-90">{getIconForType(item.type, 14)}</span>
            <span className="text-[12px]">{item.name} Properties</span>
          </div>
          <button
            onClick={onClose}
            className="w-[28px] h-[26px] hover:bg-[#E81123] flex items-center justify-center"
          >
            <svg width="10" height="10" viewBox="0 0 10 10">
              <line x1="1" y1="1" x2="9" y2="9" stroke="#FFF" strokeWidth="1.2" />
              <line x1="9" y1="1" x2="1" y2="9" stroke="#FFF" strokeWidth="1.2" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex bg-[#F0F0F0] px-[8px] pt-[6px] border-b border-[#A0A0A0] flex-wrap">
          {tabs.map((t) => (
            <button
              key={t}
              onClick={() => setActive(t)}
              className={`px-[10px] h-[22px] text-[11px] border border-[#A0A0A0] -mb-[1px] ${
                active === t
                  ? 'bg-[#F0F0F0] border-b-[#F0F0F0] relative z-[1]'
                  : 'bg-[#E1E1E1] hover:bg-[#EAEAEA]'
              }`}
              style={{ marginRight: -1 }}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="p-[14px] min-h-[300px] bg-[#F0F0F0] text-[12px] text-black">
          {/* Detail-driven tabs (jobs, certs, edu, projects, contact) take priority */}
          {detail && detail.kind === 'role' && active === 'General' && <RoleGeneralTab item={item} d={detail} />}
          {detail && detail.kind === 'role' && active === 'Responsibilities' && <RoleListTab title="Example Responsibilities" items={detail.responsibilities} icon="USER" />}
          {detail && detail.kind === 'role' && active === 'Tools' && <RoleToolsTab d={detail} />}
          {detail && detail.kind === 'role' && active === 'Frameworks' && <RoleListTab title="Frameworks & Standards" items={detail.frameworks} icon="GROUP" />}
          {detail && detail.kind === 'role' && active === 'Metrics' && <RoleListTab title="Key Metrics & Achievements" items={detail.metrics} icon="GROUP" />}
          {detail && detail.kind === 'role' && active === 'Member Of' && <MemberOfTab groups={detail.memberOf} />}

          {detail && detail.kind === 'job' && active === 'General' && <UserGeneralTab item={item} p={detail.general} summary={detail.summary} />}
          {detail && detail.kind === 'job' && active === 'Member Of' && <MemberOfTab groups={detail.memberOf} />}
          {detail && detail.kind === 'cert' && active === 'General' && <CertGeneralTab item={item} p={detail.general} summary={detail.summary} />}
          {detail && detail.kind === 'cert' && active === 'Verification' && <CertVerifyTab p={detail.general} />}
          {detail && detail.kind === 'cert' && active === 'Member Of' && <MemberOfTab groups={['Certification Holders', 'Security Professionals']} />}
          {detail && detail.kind === 'edu' && active === 'General' && <EduGeneralTab item={item} p={detail.general} summary={detail.summary} />}
          {detail && detail.kind === 'edu' && active === 'Coursework' && <EduCoursesTab id={item.id} />}
          {detail && detail.kind === 'edu' && active === 'Member Of' && <MemberOfTab groups={['Alumni', 'Domain Users']} />}
          {detail && detail.kind === 'contact' && active === 'General' && <ContactGeneralTab item={item} p={detail.general} summary={detail.summary} />}
          {detail && detail.kind === 'project' && active === 'General' && <ProjectGeneralTab item={item} d={detail} />}
          {detail && detail.kind === 'project' && active === 'Operating System' && <OSDetailedTab p={detail.os} />}
          {detail && detail.kind === 'project' && active === 'Technologies' && <TechTab tech={detail.tech} />}
          {detail && detail.kind === 'project' && active === 'Repository' && <RepoTab repo={detail.repo} />}

          {/* Original "u-me" full profile */}
          {!detail && isUser && props && active === 'General' && <UserGeneralTab item={item} p={props.general} summary={props.summary} />}
          {!detail && isUser && props && active === 'Address' && <UserAddressTab p={props.address} />}
          {!detail && isUser && props && active === 'Account' && <UserAccountTab p={props.account} />}
          {!detail && isUser && props && active === 'Profile' && <UserProfileTab p={props.profile} />}
          {!detail && isUser && props && active === 'Member Of' && <MemberOfTab groups={props.memberOf} />}

          {/* Generic fallback */}
          {!detail && (!isUser || !props) && active === 'General' && <GenericGeneralTab item={item} typeLabels={typeLabels} />}
          {!detail && isGroup && active === 'Members' && <MembersTab />}
          {!detail && isGroup && active === 'Member Of' && <MemberOfTab groups={[`Domain Users`, `${item.name} Admins`]} />}
          {!detail && isComputer && active === 'Operating System' && <OSTab item={item} />}
          {!detail && isOU && active === 'Managed By' && <ManagedByTab />}
          {!detail && isOU && active === 'Group Policy' && <GroupPolicyTab />}

          {/* Not-configured fallback */}
          {!['General', 'Address', 'Account', 'Profile', 'Member Of', 'Members', 'Operating System', 'Managed By', 'Group Policy', 'Verification', 'Coursework', 'Technologies', 'Repository', 'Responsibilities', 'Tools', 'Frameworks', 'Metrics'].includes(active) && (
            <div className="text-gray-600 italic">This tab is not configured for this object.</div>
          )}
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-[6px] bg-[#F0F0F0] px-[12px] py-[10px] border-t border-[#D0D0D0]">
          <DialogButton onClick={onClose}>OK</DialogButton>
          <DialogButton onClick={onClose}>Cancel</DialogButton>
          <DialogButton disabled>Apply</DialogButton>
          <DialogButton disabled>Help</DialogButton>
        </div>
      </div>
    </div>
  );
};

const DialogButton = ({ children, onClick, disabled }) => (
  <button
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    className={`min-w-[75px] h-[23px] text-[12px] border ${
      disabled ? 'border-[#C0C0C0] text-[#999]' : 'border-[#707070] hover:border-[#2B579A] hover:bg-[#DCE9F9] active:bg-[#B0CEEC]'
    } bg-[#F0F0F0]`}
  >
    {children}
  </button>
);

const Row = ({ label, value, children }) => (
  <div className="flex items-center mb-[8px]">
    <div className="w-[110px] text-[12px]">{label}:</div>
    <div className="flex-1">
      {children || (
        <div className="h-[20px] border border-[#8B8B8B] bg-white px-[4px] text-[12px] flex items-center">
          {value || ''}
        </div>
      )}
    </div>
  </div>
);

const UserGeneralTab = ({ item, p, summary }) => (
  <div>
    <div className="flex items-start mb-[14px] pb-[10px] border-b border-[#D0D0D0]">
      <div className="mr-[10px] scale-[2.2] origin-top-left">{getIconForType('USER', 16)}</div>
      <div className="ml-[24px] font-bold text-[13px]">{p.displayName}</div>
    </div>
    <div className="grid grid-cols-2 gap-x-[10px]">
      <Row label="First name" value={p.firstName} />
      <Row label="Initials" value="" />
    </div>
    <Row label="Last name" value={p.lastName} />
    <Row label="Display name" value={p.displayName} />
    <Row label="Description" value={p.description} />
    <Row label="Office" value={p.office} />
    <div className="mt-[6px] border-t border-[#D0D0D0] pt-[8px]">
      <Row label="Telephone number" value={p.telephone} />
      <Row label="E-mail" value={p.email} />
      <Row label="Web page" value={p.webPage} />
    </div>
    {summary && (
      <div className="mt-[6px] text-[11px] text-[#333] bg-[#FFFFE0] border border-[#D0D0A0] p-[6px]">
        <div className="font-bold mb-[2px]">Notes</div>
        <div>{summary}</div>
      </div>
    )}
  </div>
);

const UserAddressTab = ({ p }) => (
  <div>
    <Row label="Street" value={p.street} />
    <Row label="P.O. Box" value="" />
    <Row label="City" value={p.city} />
    <Row label="State/province" value={p.state} />
    <Row label="Zip/Postal Code" value={p.zip} />
    <Row label="Country/region" value={p.country} />
  </div>
);

const UserAccountTab = ({ p }) => (
  <div>
    <Row label="User logon name" value={p.logonName} />
    <Row label="Pre-Windows 2000" value={p.logonNamePre} />
    <Row label="Account expires" value={p.accountExpires} />
    <Row label="Password last set" value={p.passwordLastSet} />
    <div className="mt-[8px] border border-[#8B8B8B] bg-white p-[6px] text-[11px] max-h-[140px] overflow-auto">
      <div className="font-bold mb-[4px]">Account options:</div>
      {p.accountOptions.map((o, i) => (
        <div key={i} className="flex items-center gap-[6px]">
          <input type="checkbox" checked={o.includes(': No') ? false : true} readOnly className="w-[13px] h-[13px]" />
          <span>{o.split(':')[0]}</span>
        </div>
      ))}
    </div>
  </div>
);

const UserProfileTab = ({ p }) => (
  <div>
    <div className="font-bold text-[12px] mb-[6px]">User profile</div>
    <Row label="Profile path" value={p.profilePath} />
    <Row label="Logon script" value={p.logonScript} />
    <div className="font-bold text-[12px] mt-[10px] mb-[6px]">Home folder</div>
    <Row label="Connect" value="Z:" />
    <Row label="To" value={p.homeFolder} />
  </div>
);

const MemberOfTab = ({ groups }) => (
  <div>
    <div className="text-[12px] mb-[6px]">Member of:</div>
    <div className="border border-[#8B8B8B] bg-white h-[160px] overflow-auto">
      <div className="flex bg-[#F0F0F0] border-b border-[#A0A0A0] text-[11px]">
        <div className="px-[6px] py-[2px] w-[180px] border-r border-[#A0A0A0]">Name</div>
        <div className="px-[6px] py-[2px] flex-1">Active Directory Domain Services Folder</div>
      </div>
      {groups.map((g, i) => (
        <div key={i} className="flex text-[11px] hover:bg-[#E8F0FE]">
          <div className="px-[6px] py-[2px] w-[180px] flex items-center gap-[4px]">
            {getIconForType('GROUP', 12)}
            {g}
          </div>
          <div className="px-[6px] py-[2px] flex-1">yourname.local/Users</div>
        </div>
      ))}
    </div>
  </div>
);

const MembersTab = () => (
  <div className="text-[11px]">
    <div className="text-[12px] mb-[6px]">Members:</div>
    <div className="border border-[#8B8B8B] bg-white h-[180px] overflow-auto p-[6px]">
      <div className="flex items-center gap-[4px]">{getIconForType('USER', 12)} Your Name</div>
    </div>
  </div>
);

const OSTab = ({ item }) => (
  <div>
    <Row label="Operating system" value="Windows Server 2019 Standard" />
    <Row label="Version" value="10.0 (17763)" />
    <Row label="Service pack" value="" />
    <div className="text-[11px] text-[#555] mt-[8px]">{item.description}</div>
  </div>
);

const ManagedByTab = () => (
  <div>
    <Row label="Name" value="yourname.local/Users/Your Name" />
    <Row label="Office" value="Remote" />
    <Row label="Street" value="" />
    <Row label="City" value="Metropolis" />
    <Row label="Phone" value="+1 (555) 123-4567" />
  </div>
);

const GroupPolicyTab = () => (
  <div>
    <div className="text-[12px] mb-[6px]">Group Policy Object Links:</div>
    <div className="border border-[#8B8B8B] bg-white h-[140px] overflow-auto text-[11px]">
      <div className="flex bg-[#F0F0F0] border-b border-[#A0A0A0]">
        <div className="px-[6px] py-[2px] flex-1 border-r border-[#A0A0A0]">Group Policy Object Links</div>
        <div className="px-[6px] py-[2px] w-[80px] border-r border-[#A0A0A0]">No Override</div>
        <div className="px-[6px] py-[2px] w-[70px]">Disabled</div>
      </div>
      {['Default Domain Policy', 'Tiered Admin GPO', 'LAPS Deployment'].map((n, i) => (
        <div key={i} className="flex hover:bg-[#E8F0FE]">
          <div className="px-[6px] py-[2px] flex-1 border-r border-[#A0A0A0]">{n}</div>
          <div className="px-[6px] py-[2px] w-[80px] border-r border-[#A0A0A0]">No</div>
          <div className="px-[6px] py-[2px] w-[70px]">No</div>
        </div>
      ))}
    </div>
  </div>
);

const GenericGeneralTab = ({ item, typeLabels }) => (
  <div>
    <div className="flex items-start mb-[14px] pb-[10px] border-b border-[#D0D0D0]">
      <div className="scale-[2] origin-top-left">{getIconForType(item.type, 16)}</div>
      <div className="ml-[30px] font-bold text-[13px]">{item.name}</div>
    </div>
    <Row label="Name" value={item.name} />
    <Row label="Type" value={typeLabels[item.type] || item.type} />
    <Row label="Description" value={item.description || ''} />
    <Row label="Canonical name" value={`yourname.local/${item.name}`} />
  </div>
);

// ===== Certification tabs =====
const CertGeneralTab = ({ item, p, summary }) => (
  <div>
    <div className="flex items-start mb-[14px] pb-[10px] border-b border-[#D0D0D0]">
      <div className="scale-[2] origin-top-left">{getIconForType('USER', 16)}</div>
      <div className="ml-[30px] font-bold text-[13px]">{p.displayName}</div>
    </div>
    <Row label="Certification" value={p.displayName} />
    <Row label="Issuer" value={p.issuer} />
    <Row label="Issued" value={p.issued} />
    <Row label="Expires" value={p.expires} />
    <Row label="Credential ID" value={p.credentialId} />
    <Row label="Description" value={p.description} />
    {summary && (
      <div className="mt-[6px] text-[11px] text-[#333] bg-[#FFFFE0] border border-[#D0D0A0] p-[6px]">
        <div className="font-bold mb-[2px]">Notes</div>
        <div>{summary}</div>
      </div>
    )}
  </div>
);

const CertVerifyTab = ({ p }) => (
  <div>
    <div className="text-[12px] mb-[10px]">Click the verification URL to validate this credential with the issuer.</div>
    <Row label="Issuer" value={p.issuer} />
    <Row label="Credential ID" value={p.credentialId} />
    <Row label="Verify URL" value={p.verify || 'Not available'} />
    <div className="mt-[10px] flex">
      <div className="w-[110px]" />
      <button
        onClick={() => p.verify && window.open(p.verify, '_blank')}
        className="h-[23px] px-[12px] text-[12px] border border-[#707070] bg-[#F0F0F0] hover:border-[#2B579A] hover:bg-[#DCE9F9]"
        disabled={!p.verify}
      >
        Open Verification Page
      </button>
    </div>
  </div>
);

// ===== Education tabs =====
const EduGeneralTab = ({ item, p, summary }) => (
  <div>
    <div className="flex items-start mb-[14px] pb-[10px] border-b border-[#D0D0D0]">
      <div className="scale-[2] origin-top-left">{getIconForType('USER', 16)}</div>
      <div className="ml-[30px] font-bold text-[13px]">{p.displayName}</div>
    </div>
    <Row label="Degree" value={p.displayName} />
    <Row label="Institution" value={p.institution} />
    <Row label="Dates" value={p.dates} />
    <Row label="GPA" value={p.gpa} />
    <Row label="Honors" value={p.honors} />
    <Row label="Description" value={p.description} />
    {summary && (
      <div className="mt-[6px] text-[11px] text-[#333] bg-[#FFFFE0] border border-[#D0D0A0] p-[6px]">
        <div className="font-bold mb-[2px]">Notes</div>
        <div>{summary}</div>
      </div>
    )}
  </div>
);

const EDU_COURSES = {
  'edu-1': [
    'Cryptography & Applied Cryptographic Engineering',
    'Network Security & Intrusion Detection',
    'Digital Forensics & Incident Response',
    'Secure Software Development',
    'Risk Management & Compliance (NIST / ISO 27001)',
    'Ethical Hacking & Penetration Testing',
    'Security Architecture & Engineering',
    'Capstone: Python SIEM Correlation Engine',
  ],
  'edu-2': [
    'Introduction to Programming (C / Python)',
    'Networking I & II (Cisco CCNA track)',
    'Windows & Linux Systems Administration',
    'Database Management (SQL)',
    'Web Technologies',
    'Discrete Mathematics',
  ],
};

const EduCoursesTab = ({ id }) => {
  const courses = EDU_COURSES[id] || [];
  return (
    <div>
      <div className="text-[12px] mb-[6px]">Coursework:</div>
      <div className="border border-[#8B8B8B] bg-white h-[200px] overflow-auto">
        {courses.map((c, i) => (
          <div key={i} className="flex items-center gap-[6px] px-[6px] py-[2px] text-[11px] hover:bg-[#E8F0FE]">
            {getIconForType('GROUP', 12)}
            <span>{c}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ===== Contact tab =====
const ContactGeneralTab = ({ item, p, summary }) => (
  <div>
    <div className="flex items-start mb-[14px] pb-[10px] border-b border-[#D0D0D0]">
      <div className="scale-[2] origin-top-left">{getIconForType('USER', 16)}</div>
      <div className="ml-[30px] font-bold text-[13px]">{p.displayName}</div>
    </div>
    <Row label="Type" value={p.displayName} />
    <Row label="Value" value={p.value} />
    <div className="mt-[10px] flex">
      <div className="w-[110px]" />
      <button
        onClick={() => p.href && p.href !== '#' && window.open(p.href, '_blank')}
        className="h-[23px] px-[12px] text-[12px] border border-[#707070] bg-[#F0F0F0] hover:border-[#2B579A] hover:bg-[#DCE9F9]"
        disabled={!p.href || p.href === '#'}
      >
        Open
      </button>
    </div>
    {summary && (
      <div className="mt-[12px] text-[11px] text-[#333] bg-[#FFFFE0] border border-[#D0D0A0] p-[6px]">
        <div className="font-bold mb-[2px]">Notes</div>
        <div>{summary}</div>
      </div>
    )}
  </div>
);

// ===== Project tabs =====
const ProjectGeneralTab = ({ item, d }) => (
  <div>
    <div className="flex items-start mb-[14px] pb-[10px] border-b border-[#D0D0D0]">
      <div className="scale-[2] origin-top-left">{getIconForType('COMPUTER', 16)}</div>
      <div className="ml-[30px] font-bold text-[13px]">{d.general.name}</div>
    </div>
    <Row label="Computer name" value={d.general.name} />
    <Row label="DNS name" value={d.general.dnsName} />
    <Row label="Role" value={d.general.role} />
    <Row label="Description" value={item.description || ''} />
    {d.summary && (
      <div className="mt-[6px] text-[11px] text-[#333] bg-[#FFFFE0] border border-[#D0D0A0] p-[6px]">
        <div className="font-bold mb-[2px]">Notes</div>
        <div>{d.summary}</div>
      </div>
    )}
  </div>
);

const OSDetailedTab = ({ p }) => (
  <div>
    <Row label="Operating system" value={p.os} />
    <Row label="Version" value={p.version} />
    <Row label="Service pack" value={p.servicePack || ''} />
  </div>
);

const TechTab = ({ tech }) => (
  <div>
    <div className="text-[12px] mb-[6px]">Technologies used:</div>
    <div className="border border-[#8B8B8B] bg-white h-[200px] overflow-auto">
      {tech.map((t, i) => (
        <div key={i} className="flex items-center gap-[6px] px-[6px] py-[2px] text-[11px] hover:bg-[#E8F0FE]">
          {getIconForType('GROUP', 12)}
          <span>{t}</span>
        </div>
      ))}
    </div>
  </div>
);

const RepoTab = ({ repo }) => (
  <div>
    <Row label="Repository URL" value={repo} />
    <div className="mt-[10px] flex">
      <div className="w-[110px]" />
      <button
        onClick={() => window.open(repo, '_blank')}
        className="h-[23px] px-[12px] text-[12px] border border-[#707070] bg-[#F0F0F0] hover:border-[#2B579A] hover:bg-[#DCE9F9]"
      >
        Open on GitHub
      </button>
    </div>
  </div>
);

// ===== Role tabs (rich profile) =====
const RoleGeneralTab = ({ item, d }) => (
  <div>
    <div className="flex items-start mb-[14px] pb-[10px] border-b border-[#D0D0D0]">
      <div className="scale-[2] origin-top-left">{getIconForType('USER', 16)}</div>
      <div className="ml-[30px]">
        <div className="font-bold text-[13px]">{d.general.displayName}</div>
        <div className="text-[11px] text-[#666] mt-[2px]">{d.general.description}</div>
      </div>
    </div>
    <Row label="Display name" value={d.general.displayName} />
    <Row label="Description" value={d.general.description} />
    <Row label="E-mail" value={d.general.email} />
    <Row label="Telephone" value={d.general.telephone} />
    <Row label="Office" value={d.general.office} />

    <div className="mt-[6px] text-[11px] text-[#333] bg-[#FFFFE0] border border-[#D0D0A0] p-[6px]">
      <div className="font-bold mb-[2px]">Role Summary</div>
      <div>{d.summary}</div>
    </div>

    {d.competencies && d.competencies.length > 0 && (
      <div className="mt-[8px]">
        <div className="font-bold text-[12px] mb-[4px]">Core Competencies</div>
        <div className="flex flex-wrap gap-[4px]">
          {d.competencies.map((c, i) => (
            <span
              key={i}
              className="inline-block text-[11px] bg-[#E8F0FE] border border-[#A8C8E8] text-[#1F3A6B] px-[6px] py-[1px]"
            >
              {c}
            </span>
          ))}
        </div>
      </div>
    )}
  </div>
);

const RoleListTab = ({ title, items, icon }) => (
  <div>
    <div className="text-[12px] mb-[6px]">{title}:</div>
    <div className="border border-[#8B8B8B] bg-white max-h-[260px] overflow-auto">
      {(items || []).map((it, i) => (
        <div
          key={i}
          className="flex items-center gap-[6px] px-[6px] py-[3px] text-[11.5px] border-b border-[#EEE] last:border-b-0 hover:bg-[#E8F0FE]"
        >
          {getIconForType(icon || 'GROUP', 12)}
          <span>{it}</span>
        </div>
      ))}
    </div>
  </div>
);

const RoleToolsTab = ({ d }) => (
  <div>
    <div className="text-[12px] mb-[6px]">Tools &amp; Technologies:</div>
    <div className="border border-[#8B8B8B] bg-white p-[6px] mb-[10px]">
      <div className="flex flex-wrap gap-[4px]">
        {(d.tools || []).map((t, i) => (
          <span
            key={i}
            className="inline-block text-[11px] bg-[#E8F0FE] border border-[#A8C8E8] text-[#1F3A6B] px-[6px] py-[1px]"
          >
            {t}
          </span>
        ))}
      </div>
    </div>
    {d.technicalSkills && d.technicalSkills.length > 0 && (
      <>
        <div className="text-[12px] mb-[6px]">Technical Skills:</div>
        <div className="border border-[#8B8B8B] bg-white max-h-[200px] overflow-auto">
          {d.technicalSkills.map((s, i) => (
            <div
              key={i}
              className="flex items-center gap-[6px] px-[6px] py-[2px] text-[11.5px] border-b border-[#EEE] last:border-b-0 hover:bg-[#E8F0FE]"
            >
              {getIconForType('GROUP', 12)}
              <span>{s}</span>
            </div>
          ))}
        </div>
      </>
    )}
  </div>
);

export default PropertiesDialog;
